import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Card,
  Col,
  Row,
  Table,
  Tag,
  Button,
  Input,
  Select,
  DatePicker,
  Space,
  Statistic,
  Popover,
  Typography,
  Dropdown,
} from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { getBirdData } from '../elements/SpeciesBirdIdentificationCSV';
import SpeciesBirdIdentificationMap from '../map/SpeciesBirdIdentificationMap';
import { useKeycloak } from '@react-keycloak/web';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

const { Search: AntSearch } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

export interface BirdData {
  key: number;
  sensorId: string;
  timestamp: string;
  commonName: string;
  scientificName: string;
  identificationProbability: number;
  conservationStatus?: string;
  protectionStatus?: string[];
  controlOrStudyarea?: string;
}

export interface SensorStats {
  sensorId: string;
  totalRecordings: number;
  uniqueSpecies: number;
  avgProbability: number;
  lastRecording: string;
  recordings: BirdData[];
}

const SpeciesBirdIdentification: React.FC = () => {
  // State management
  const [selectedSensorId, setSelectedSensorId] = useState<string | null>(null);
  const [hoveredSensorId, setHoveredSensorId] = useState<string | null>(null);
  const [monitoringData, setMonitoringData] = useState<BirdData[]>([]);
  const [loading, setLoading] = useState(true);
  const [greeceLocation, setGreeceLocation] = useState<string | null>(null);
  const [eviaData, setEviaData] = useState<BirdData[]>([]);
  const [thraceData, setThraceData] = useState<BirdData[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  // Filter states
  const [searchText, setSearchText] = useState('');
  const [selectedSensorFilter, setSelectedSensorFilter] = useState<string | undefined>(undefined);
  const [selectedConservationStatus, setSelectedConservationStatus] = useState<string | undefined>(undefined);
  const [selectedControlOrStudyArea, setSelectedControlOrStudyArea] = useState<string | undefined>(undefined); // New filter state
  const [dateRange, setDateRange] = useState<any>(null);
  const { keycloak } = useKeycloak(); // Get the keycloak instance
  const userCaseStudy = keycloak.idTokenParsed?.case_study as string | undefined;

  useEffect(() => {
    const loadInitialData = async () => {
      // setLoading(true);
      try {
        if (userCaseStudy === 'Cyprus') {
          const data = await getBirdData();
          setMonitoringData(data);
        }
      } catch (error) {
        console.error('Error loading bird data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [userCaseStudy]);

  useEffect(() => {
    if (userCaseStudy === 'Greece') {
      if (greeceLocation === 'Evia') {
        setMonitoringData(eviaData);
      } else if (greeceLocation === 'Thrace') {
        setMonitoringData(thraceData);
      }
    }
  }, [greeceLocation, eviaData, thraceData, userCaseStudy]);

  // Memoized filtered data
  const filteredData = useMemo(() => {
    let filtered = [...monitoringData];

    // Text search
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          (item.commonName && item.commonName.toLowerCase().includes(searchLower)) ||
          item.scientificName.toLowerCase().includes(searchLower) ||
          item.sensorId.toLowerCase().includes(searchLower),
      );
    }

    // Sensor filter
    if (selectedSensorFilter) {
      filtered = filtered.filter((item) => item.sensorId === selectedSensorFilter);
    }

    // Conservation status filter
    if (selectedConservationStatus) {
      filtered = filtered.filter((item) => item.conservationStatus === selectedConservationStatus);
    }
    // Control/Study area filter
    if (selectedControlOrStudyArea) {
      filtered = filtered.filter(
        (item) => item.controlOrStudyarea === selectedControlOrStudyArea
      );
    }
    // Date range filter
    if (dateRange && dateRange.length === 2) {
      const [startDate, endDate] = dateRange;
      filtered = filtered.filter((item) => {
        const itemDate = new Date(item.timestamp);
        return itemDate >= startDate.toDate() && itemDate <= endDate.toDate();
      });
    }

    return filtered;
  }, [monitoringData, searchText, selectedSensorFilter, selectedConservationStatus, selectedControlOrStudyArea, dateRange]);

  // Memoized sensor statistics
  const sensorStats = useMemo((): Record<string, SensorStats> => {
    const stats: Record<string, SensorStats> = {};

    filteredData.forEach((item) => {
      if (!stats[item.sensorId]) {
        stats[item.sensorId] = {
          sensorId: item.sensorId,
          totalRecordings: 0,
          uniqueSpecies: 0,
          avgProbability: 0,
          lastRecording: '',
          recordings: [],
        };
      }
      stats[item.sensorId].recordings.push(item);
    });

    // Calculate derived statistics
    Object.values(stats).forEach((stat) => {
      stat.totalRecordings = stat.recordings.length;
      stat.uniqueSpecies = new Set(stat.recordings.map((r) => r.commonName)).size;
      stat.avgProbability = Math.round(
        stat.recordings.reduce((sum, r) => sum + r.identificationProbability, 0) / stat.totalRecordings,
      );
      stat.lastRecording =
        stat.recordings.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0]
          ?.timestamp || '';
    });

    return stats;
  }, [filteredData]);

  // Prepare data for the bar chart
  const chartData = useMemo(() => {
    const speciesCounts: { [key: string]: number } = {};
    filteredData.forEach((item) => {
      // Use commonName if available, otherwise fall back to scientificName
      const nameToUse = item.commonName && item.commonName.trim() !== '' ? item.commonName : item.scientificName;
      speciesCounts[nameToUse] = (speciesCounts[nameToUse] || 0) + 1;
    });
    // Convert to array of objects for Recharts
    // Sort by count in descending order and take top N (e.g., 10) for better visualization
    return Object.entries(speciesCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10); // Display top 10 species by count
  }, [filteredData]);

  // Get unique values for filters
  const uniqueSensors = useMemo(
    () => [...new Set(monitoringData.map((item) => item.sensorId))].sort(),
    [monitoringData],
  );

  const uniqueConservationStatus = useMemo(
    () => [...new Set(monitoringData.map((item) => item.conservationStatus))].sort(),
    [monitoringData],
  );
  const uniqueControlOrStudyAreas = useMemo( // New: Get unique controlOrStudyarea values
    () => [...new Set(monitoringData.map((item) => item.controlOrStudyarea))].filter(Boolean).sort() as string[],
    [monitoringData],
  );
  // Event handlers
  const handleSensorSelect = useCallback((sensorId: string | null) => {
    setSelectedSensorId(sensorId);
  }, []);

  const handleSensorHover = useCallback((sensorId: string | null) => {
    setHoveredSensorId(sensorId);
  }, []);

  const handleRowClick = useCallback(
    (record: BirdData) => {
      const newSelection = selectedSensorId === record.sensorId ? null : record.sensorId;
      setSelectedSensorId(newSelection);
    },
    [selectedSensorId],
  );

  const clearFilters = useCallback(() => {
    setSearchText('');
    setSelectedSensorFilter(undefined);
    setSelectedConservationStatus(undefined);
    setSelectedControlOrStudyArea(undefined);
    setDateRange(null);
  }, []);

  // Helper function to render protection status with full visibility
  const renderProtectionStatus = (tags: string[]) => {
    if (!tags || tags.length === 0) {
      return <Tag color='default'>No Protection Status</Tag>;
    }

    // If 2 or fewer tags, show them all normally
    if (tags.length <= 2) {
      return (
        <>
          {tags.map((tag, index) => (
            <Tag color='blue' key={`${tag}-${index}`} style={{ marginBottom: 2 }}>
              {tag.length > 25 ? `${tag.substring(0, 25)}...` : tag}
            </Tag>
          ))}
        </>
      );
    }

    // If more than 2 tags, show first 2 and create a popover for the rest
    const visibleTags = tags.slice(0, 2);
    const hiddenTags = tags.slice(2);

    const popoverContent = (
      <div style={{ maxWidth: 300 }}>
        <div style={{ marginBottom: 8, fontWeight: 'bold' }}>All Protection Statuses:</div>
        {tags.map((tag, index) => (
          <Tag color='blue' key={`${tag}-${index}`} style={{ marginBottom: 4, display: 'block', whiteSpace: 'normal' }}>
            {tag}
          </Tag>
        ))}
      </div>
    );

    return (
      <>
        {visibleTags.map((tag, index) => (
          <Tag color='blue' key={`${tag}-${index}`} style={{ marginBottom: 2 }}>
            {tag.length > 25 ? `${tag.substring(0, 25)}...` : tag}
          </Tag>
        ))}
        <Popover content={popoverContent} title='Protection Status Details' trigger='hover' placement='topLeft'>
          <Tag
            color='geekblue'
            style={{
              marginBottom: 2,
              cursor: 'pointer',
              borderStyle: 'dashed',
            }}
          >
            +{hiddenTags.length} more
          </Tag>
        </Popover>
      </>
    );
  };

  // Table columns configuration
  const columns = [
    {
      title: 'Sensor ID',
      dataIndex: 'sensorId',
      key: 'sensorId',
      sorter: (a: BirdData, b: BirdData) => a.sensorId.localeCompare(b.sensorId),
    },
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      sorter: (a: BirdData, b: BirdData) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
      render: (text: string) => {
        const date = new Date(text);
        // Check if the date is valid before formatting
        if (isNaN(date.getTime())) {
          return 'Invalid Date'; // Or some other fallback for invalid dates
        }
        const day = date.getDate();
        const month = date.toLocaleDateString('en-US', { month: 'long' });
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${day} ${month} ${year} ${hours}:${minutes}:${seconds}`;
      },
    },
    {
      title: 'Common Name',
      dataIndex: 'commonName',
      key: 'commonName',
      sorter: (a: BirdData, b: BirdData) => (a.commonName ?? '').localeCompare(b.commonName ?? ''),
    },
    {
      title: 'Scientific Name',
      dataIndex: 'scientificName',
      key: 'scientificName',
      sorter: (a: BirdData, b: BirdData) => a.scientificName.localeCompare(b.scientificName),
    },
    {
      title: 'Probability',
      dataIndex: 'identificationProbability',
      key: 'identificationProbability',
      sorter: (a: BirdData, b: BirdData) => a.identificationProbability - b.identificationProbability,
      render: (probability: number) => `${probability.toFixed(2)}%`,
    },
    {
      title: 'Conservation Status',
      dataIndex: 'conservationStatus',
      key: 'conservationStatus',
      sorter: (a: BirdData, b: BirdData) => (a.conservationStatus ?? '').localeCompare(b.conservationStatus ?? ''),
      render: (status: string | undefined) => { // Type status as string | undefined
        if (!status) {
          return <Tag color='default'>N/A</Tag>; // Handle undefined status
        }
        const colorMap: Record<string, string> = {
          'Endangered': 'volcano',
          'Near Threatened': 'orange',
          'Least Concern': 'green',
          'Vulnerable': 'gold',
        };
        return <Tag color={colorMap[status] || 'geekblue'}>{status.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Protection Status',
      dataIndex: 'protectionStatus',
      key: 'protectionStatus',
      sorter: (a: BirdData, b: BirdData) =>
        (Array.isArray(a.protectionStatus) ? a.protectionStatus.join(', ') : (a.protectionStatus ?? ''))
          .localeCompare(Array.isArray(b.protectionStatus) ? b.protectionStatus.join(', ') : (b.protectionStatus ?? '')),
      render: renderProtectionStatus,
    },
    {
      title: 'Control/Study Area', // New column for controlOrStudyarea
      dataIndex: 'controlOrStudyarea',
      key: 'controlOrStudyarea',
      sorter: (a: BirdData, b: BirdData) => (a.controlOrStudyarea ?? '').localeCompare(b.controlOrStudyarea ?? ''),
      render: (area: string | undefined) => area || 'N/A',
    },
  ];
  const handleGreeceLocationChange = (e: { target: { value: React.SetStateAction<string | null>; }; }) => {
    setGreeceLocation(e.target.value);
  };

  const locations = [
    {
      key: 'Evia',
      label: 'Evia',
    },
    {
      key: 'Thrace',
      label: 'Thrace',
    },
  ];

  const handleMenuClick = (e: { key: React.SetStateAction<string | null>; }) => {
    // This now correctly calls the defined function
    handleGreeceLocationChange({ target: { value: e.key } });
  };

  const menuProps = {
    items: locations,
    onClick: handleMenuClick,
  };


  return (
    <div>
      <h1 style={{ marginBottom: '20px', color: '#329619', fontSize: '25px' }}>
        Home &gt; Species &gt; Bird Identification / Acoustic Monitoring
      </h1>

      <Row gutter={[16, 16]}>
        {/* "Select a location" component moved to its own row for better structure */}
        {userCaseStudy === 'Greece' && (
          <Col span={6}>
            <Card>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Typography.Text strong>Select a location:</Typography.Text>
                <Dropdown menu={menuProps}>
                  <Button style={{ width: '100%', textAlign: 'left' }}>
                    {greeceLocation || '--- Select ---'}
                    <DownOutlined id='SelectAreaGreeceBirdsId' style={{ float: 'right', marginTop: '4px' }} />
                  </Button>
                </Dropdown>
              </div>
            </Card>
          </Col>
        )}

        {/* Summary Statistics */}
        <Col span={24}>
          <Row gutter={16}>
            <Col xs={12} sm={6}>
              <Card id='StatsTotalRecordingsBirdsId'>
                <Statistic title='Total Recordings' value={filteredData.length} suffix={`/ ${monitoringData.length.toLocaleString('en-US')}`}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card id='StatsActiveSensorsBirdsId'>
                <Statistic title='Active Sensors' value={Object.keys(sensorStats).length} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card id='StatsUniqueSpeciesBirdsId'>
                <Statistic title='Unique Species' value={new Set(filteredData.map((item) => item.scientificName)).size} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card id='StatsAverageConfidenceBirdsId'>
                <Statistic
                  title='Avg. Confidence'
                  value={
                    filteredData.length > 0
                      ? Math.round(
                        filteredData.reduce((sum, item) => sum + item.identificationProbability, 0) /
                        filteredData.length,
                      )
                      : 0
                  }
                  suffix='%'
                />
              </Card>
            </Col>
          </Row>
        </Col>

        {/* Map Section */}
        <Col span={24}>
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Sensor Network Map</span>
                <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#666' }}>
                  {selectedSensorId ? `Selected: ${selectedSensorId}` : 'Click sensors to view details'}
                </span>
              </div>
            }
            style={{ marginBottom: '16px' }}
          >
            <SpeciesBirdIdentificationMap
              sensorStats={sensorStats}
              selectedSensorId={selectedSensorId}
              hoveredSensorId={hoveredSensorId}
              onSensorSelect={handleSensorSelect}
              onSensorHover={handleSensorHover}
              location={greeceLocation}
            />
          </Card>
        </Col>

        {/* Data Table Section */}
        <Col span={24}>
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Detection Records</span>
                <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#666' }}>
                  {filteredData.length} of {monitoringData.length} records
                  {selectedSensorId && ` (Sensor: ${selectedSensorId})`}
                </span>
              </div>
            }
          >
            {/* Filters Section */}
            <Card size='small' title='Filters' style={{ marginBottom: '16px', backgroundColor: '#fafafa' }}>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={6}>
                  <AntSearch
                    id='SearchBarBirdsId'
                    placeholder='Search birds, sensors...'
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    allowClear
                  />
                </Col>
                <Col xs={12} sm={6} md={3}>
                  <Select
                    id='SelectSensorBirdsId'
                    placeholder='Select Sensor'
                    value={selectedSensorFilter}
                    onChange={setSelectedSensorFilter}
                    style={{ width: '100%' }}
                    allowClear
                  >
                    {uniqueSensors
                      .filter(Boolean)
                      .map((sensor) => (
                        <Option key={sensor} value={sensor}>
                          {sensor} ({sensorStats[sensor]?.totalRecordings || 0} records)
                        </Option>
                      ))}
                  </Select>
                </Col>
                <Col xs={12} sm={6} md={3}>
                  <Select
                    id='ConservationStatusBirdsId'
                    placeholder='Conservation Status'
                    value={selectedConservationStatus}
                    onChange={setSelectedConservationStatus}
                    style={{ width: '100%' }}
                    allowClear
                  >
                    {uniqueConservationStatus
                      .filter(Boolean)
                      .map((status) => (
                        <Option key={status} value={status}>
                          {status}
                        </Option>
                      ))}
                  </Select>
                </Col>
                <Col xs={12} sm={6} md={3}>
                  <Select // New Select component for controlOrStudyarea
                    id='ControlOrStudyAreaBirdsId'
                    placeholder='Control/Study Area'
                    value={selectedControlOrStudyArea}
                    onChange={setSelectedControlOrStudyArea}
                    style={{ width: '100%' }}
                    allowClear
                  >
                    {uniqueControlOrStudyAreas
                      .map((area) => (
                        <Option key={area} value={area}>
                          {area}
                        </Option>
                      ))}
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Space>
                    <RangePicker id={"FilterDateBirdsId"} value={dateRange} onChange={setDateRange} showTime format='YYYY-MM-DD HH:mm' />
                    <Button onClick={clearFilters} type='link' size='small'>
                      Clear All
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Card>

            <Table
              id='TableBirdsId'
              columns={columns}
              dataSource={filteredData}
              rowKey='key'
              loading={loading}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
                onChange: (page, pageSize) => {
                  setPagination({ current: page, pageSize });
                },
              }}
              rowClassName={(record) => (selectedSensorId === record.sensorId ? 'selected-row' : '')}
              onRow={(record) => ({
                onMouseEnter: () => setHoveredSensorId(record.sensorId),
                onMouseLeave: () => setHoveredSensorId(null),
                onClick: () => handleRowClick(record),
                style: {
                  cursor: 'pointer',
                  backgroundColor:
                    selectedSensorId === record.sensorId
                      ? '#f6ffed'
                      : hoveredSensorId === record.sensorId
                        ? '#f6ffed'
                        : 'transparent',
                },
              })}
            />
          </Card>
        </Col>
        {/* Bar Chart Section */}
        <Col span={24}>
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Top 10 Species Detections by Common Name</span>
                <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#666' }}>
                  Visualizing common names and their prediction counts
                </span>
              </div>
            }
            style={{ marginTop: '16px' }}
          >
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={chartData}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} interval={0} />
                  <YAxis label={{ value: 'Number of Detections', angle: -90, position: 'insideLeft' }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#8884d8" name="Number of Detections" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography.Text disabled>No data available for chart. Apply filters or load data.</Typography.Text>
            )}
          </Card>
        </Col>
      </Row>
    </div >
  );
}

export { SpeciesBirdIdentification }